import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "insane-pair-cart";
export const MAX_PAIRS = 5;
export const BUNDLE_PRICE = 999;

const load = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const add = (product) => {
      if (count >= MAX_PAIRS) return false;
      const key = product.id;
      setItems((prev) => {
        const found = prev.find((i) => i.key === key);
        if (found) return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
        return [...prev, { key, product_id: product.id, name: product.name, price: product.price, image: product.image, quantity: 1 }];
      });
      return true;
    };
    const setQty = (key, q) => {
      const current = items.find((i) => i.key === key)?.quantity ?? 0;
      if (q > current && count - current + q > MAX_PAIRS) return false;
      setItems((prev) => (q < 1 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, quantity: q } : i))));
      return true;
    };
    const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
    const clear = () => setItems([]);
    const sync = (products) =>
      setItems((prev) => {
        const merged = {};
        let left = MAX_PAIRS;
        prev.forEach((i) => {
          const p = products.find((x) => x.id === i.product_id);
          if (!p || left <= 0) return;
          const q = Math.min(i.quantity, left);
          left -= q;
          merged[p.id] = { key: p.id, product_id: p.id, name: p.name, price: p.price, image: p.image, quantity: (merged[p.id]?.quantity ?? 0) + q };
        });
        return Object.values(merged);
      });
    const fullPrice = items.reduce((s, i) => s + i.quantity * i.price, 0);
    const subtotal = count === MAX_PAIRS ? BUNDLE_PRICE : fullPrice;
    return { items, add, setQty, remove, clear, sync, count, fullPrice, subtotal, open, setOpen };
  }, [items, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
